import {
  Component, EventEmitter, Input, OnInit, Output, ViewChild
} from '@angular/core';
import {
  FormBuilder, FormGroup, ReactiveFormsModule, Validators
} from '@angular/forms';
import { map, Observable, of, raceWith } from 'rxjs';
import {
  CreateExportJobDTO, ExportJobDTO, ExportJobStatus, ExportJobType,
  LimitedUserInfoDTO, ReportFileType
} from 'src/app/base/models/models.swagger.generated';
import { ModalService } from 'src/app/base/shared/modal/modal.service';
import { ToastService } from 'src/app/base/services/toast.service';
import { DropDownConstantsFactory } from 'src/app/base/shared/syncfusion/dropdown-constants-factory';
import { EnumTranslationService } from 'src/app/base/services/enum-translation.service';
import { DateTime } from 'luxon';
import { ExportJobService } from '../export-job.service';
import { FileInfo, SelectedEventArgs, Uploader, UploaderModule } from '@syncfusion/ej2-angular-inputs';
import { MimeTypeHelper } from 'src/app/base/shared/mime-type.helper';
import { ExportJobFormType } from './export-job-edit-form-type';
import { TranslateModule } from '@ngx-translate/core';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { AppErrorMessagesComponent } from 'src/app/base/error-messages/app-error-messages.component';
import { DatePickerModule } from '@syncfusion/ej2-angular-calendars';
import { CommonModule } from '@angular/common';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';
import { getAllowedFileExtensions, getStatusClassName } from '../export-job-common';
import { DateUtils } from 'src/app/base/utils/date-utils';

@Component({
  selector: 'app-export-job-edit-form',
  templateUrl: './export-job-edit-form.component.html',
  styleUrls: ['./export-job-edit-form.component.scss', '../export-job.component.scss'],
  standalone: true,
  imports: [
    TranslateModule, ReactiveFormsModule, DropDownListModule,
    AppErrorMessagesComponent, DatePickerModule, CommonModule,
    UploaderModule, TooltipModule
  ]
})
export class ExportJobEditFormComponent implements OnInit {
  @Input() set exportJob(dto: ExportJobDTO) {
    this.fillFormFromDto(dto);
  }

  @Output() create = new EventEmitter<CreateExportJobDTO>();
  @Output() delete = new EventEmitter<number>();
  @Output() cancel = new EventEmitter<void>();

  @ViewChild('defaultupload') defaultupload: Uploader;

  exportJobForm: FormGroup<ExportJobFormType>;

  readonly enumDropdownSettings = DropDownConstantsFactory.createNumericEnumDropdownSettings();
  readonly calendarDepth = 'Year';
  readonly calendarStart = 'Year';

  readonly ExportJobStatus = ExportJobStatus;

  readonly jobTypes = this.enumTranslateService.translateNumericEnum(
    ExportJobType, 'i18n.domain.export-jobs.enums.export-job-type.'
  );
  readonly fileTypes = this.enumTranslateService.translateNumericEnum(
    ReportFileType, 'i18n.domain.export-jobs.enums.export-job-file-type.'
  );

  constructor(
    private readonly modalService: ModalService,
    private readonly toastService: ToastService,
    private readonly fb: FormBuilder,
    private readonly enumTranslateService: EnumTranslationService,
    private readonly exportJobService: ExportJobService
  ) {
    this.exportJobForm = this.buildForm();
  }

  ngOnInit(): void {
    this.exportJobForm.controls.type.valueChanges.subscribe(type => {
      this.handleJobTypeChange(type);
    });
  }

  get disabledDelete(): boolean {
    return this.exportJobForm.controls.status.value === ExportJobStatus.InProgress;
  }

  get allowedExtensions(): string {
    return getAllowedFileExtensions(this.exportJobForm.controls.type.value);
  }

  get statusClassName(): string {
    return getStatusClassName(this.exportJobForm.controls.status.value);
  }

  public canCloseForm(): Observable<boolean> {
    if (this.exportJobForm.dirty) {
      const dialog = this.modalService.showOkCancelDialog(
        'i18n.domain.export-jobs.edit.discard-dialog.title',
        'i18n.domain.export-jobs.edit.discard-dialog.question'
      );
      return dialog.onOkSubject.pipe(map(() => true), raceWith(dialog.onCancelSubject.pipe(map(() => false))));
    }
    return of(true);
  }

  public closeForm(): void {
    this.canCloseForm().subscribe(canClose => canClose && this.forceClose());
  }

  public onCreate(): void {
    if (this.exportJobForm.valid) {
      this.create.emit(this.getDtoFromForm());
    } else {
      this.exportJobForm.markAsDirty();
      this.toastService.showWarning(
        'i18n.domain.export-jobs.edit.form-invalid.title',
        'i18n.domain.export-jobs.edit.form-invalid.message'
      );
    }
  }

  public deleteJob(): void {
    const id = this.exportJobForm.controls.id.value;
    if (id > 0) {
      const dialog = this.modalService.showOkCancelDialog(
        'i18n.domain.export-jobs.messages.delete-confirm-title',
        'i18n.domain.export-jobs.messages.delete-confirm-message'
      );
      dialog.onOkSubject.subscribe(() => {
        this.delete.emit(id);
        this.forceClose();
      });
    } else {
      this.forceClose();
    }
  }

  public forceClose(): void {
    this.exportJobForm.reset();
    this.defaultupload?.clearAll();
    this.cancel.emit();
  }

  public downloadFile(): void {
    const id = this.exportJobForm.controls.id.value;
    if (!id) {
      return;
    }

    this.exportJobService.downloadOutputFile(id).subscribe({
      next: blob => this.triggerFileDownload(blob, id),
      error: () => this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message')
    });
  }

  public handleFileInput($event: SelectedEventArgs) {
    const files: FileInfo[] = $event.filesData;
    if (!files || files.length < 1) {
      return;
    }
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (event) => {
      let base64Data = event.target.result as string;
      const startIndex = base64Data.search(';base64,');
      if (startIndex > 0) {
        base64Data = 'data:' + MimeTypeHelper.getMimeType(file.type.toLowerCase()) + base64Data.substring(startIndex);
      } else {
        base64Data = 'data:' + MimeTypeHelper.getMimeType(file.type.toLowerCase()) + ';base64,' + base64Data;
      }
      this.exportJobForm.controls.templateBase64.setValue(base64Data);
    };
    reader.readAsDataURL(file.rawFile as File);
  }

  public onFileRemove(): void {
    this.exportJobForm.controls.templateBase64.setValue(null);
  }

  public getDisplayDateTime(date: Date): string | null {
    return date ? DateUtils.formatDateTime(date) : null;
  }

  public getUserDisplayName(user: LimitedUserInfoDTO): string | null {
    return user ? `${user.firstname} ${user.lastname}` : null;
  }

  public getStatusDisplayName(status: ExportJobStatus): string {
    return this.enumTranslateService.translateNumericEnum(
      ExportJobStatus,
      'i18n.domain.export-jobs.enums.export-job-status.'
    )[status ?? ExportJobStatus.Error]?.text;
  }

  public downloadTemplate(): void {
    this.exportJobService.downloadTemplateFile(this.exportJobForm.controls.type.value).subscribe({
      next: blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.getTemplateName(this.exportJobForm.controls.type.value);
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message')
    });
  }

  public downloadExecutionLog(): void {
    const id = this.exportJobForm.controls.id.value;
    if (!id) {
      return;
    }

    this.exportJobService.getExecutionLog(id).subscribe({
      next: log => {
        var blob = new Blob([log.logMessage], { type: 'text/plain;charset=utf-8' })
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `export-job-${id}-log.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message')
    });
  }

  // -------------------- PRIVATE HELPERS --------------------

  private getTemplateName(type: ExportJobType): string {
    switch (type) {
      case ExportJobType.ParkerExtract:
        return 'parker-extract-template.docx';
      case ExportJobType.ParkerExtractSAP:
        return 'parker-extract-sap-template.csv';
      default:
        return 'unknown-template';
    }
  }

  private buildForm(): FormGroup<ExportJobFormType> {
    return this.fb.group<ExportJobFormType>({
      id: this.fb.control(null),
      type: this.fb.control(null, Validators.required),
      fileType: this.fb.control({ value: null, disabled: true }),
      fromDate: this.fb.control(null, Validators.required),
      toDate: this.fb.control(null, Validators.required),
      status: this.fb.control(null),
      createdOn: this.fb.control(null),
      executedOn: this.fb.control(null),
      initiatedByUser: this.fb.control(null),
      purgeDataOn: this.fb.control(null),
      templateBase64: this.fb.control(null)
    });
  }

  private getDtoFromForm(): CreateExportJobDTO {
    var from = this.exportJobForm.controls.fromDate.value;
    var fromDate = new Date(Date.UTC(from.getFullYear(), from.getMonth(), from.getDate()));;
    var toDate = new Date(Date.UTC(from.getFullYear(), from.getMonth() + 1, 0));
    const { type, fileType, templateBase64 } = this.exportJobForm.controls;
    return new CreateExportJobDTO({
      type: type.value,
      fileType: fileType.value,
      clientId: null,
      fromDate: DateTime.fromJSDate(fromDate),
      toDate: DateTime.fromJSDate(toDate),
      templateBase64: templateBase64.value
    });
  }

  private fillFormFromDto(dto: ExportJobDTO): void {
    const defaultValues = {
      id: null,
      type: ExportJobType.ParkerExtract,
      fileType: ReportFileType.PDF,
      fromDate: this.getStartOfMonth(),
      toDate: this.getEndOfMonth(), status: null,
      createdOn: null,
      executedOn: null,
      initiatedByUser: null,
      purgeDataOn: null,
      templateBase64: null
    };

    const patchValues = dto?.id ? {
      id: dto.id,
      type: dto.type,
      fileType: dto.fileType,
      fromDate: dto.fromDate?.toJSDate(),
      toDate: dto.toDate?.toJSDate(),
      status: dto.status,
      createdOn: dto.createdOn?.toJSDate(),
      executedOn: dto.executedOn?.toJSDate(),
      initiatedByUser: dto.initiatedByUser,
      purgeDataOn: dto.purgeDataOn?.toJSDate(),
      templateBase64: null
    } : defaultValues;

    this.exportJobForm.patchValue(patchValues);

    const disable = !!dto?.id;
    ['type', 'fromDate', 'toDate'].forEach(control => {
      this.exportJobForm.controls[control][disable ? 'disable' : 'enable']();
    });

    this.defaultupload?.clearAll();
    this.exportJobForm.markAsPristine();
    this.exportJobForm.markAsUntouched();
  }

  private handleJobTypeChange(type: ExportJobType): void {
    const fileType =
      type === ExportJobType.ParkerExtract ? ReportFileType.PDF :
        type === ExportJobType.ParkerExtractSAP ? ReportFileType.CSV : null;
    this.exportJobForm.controls.fileType.setValue(fileType);
    this.defaultupload?.clearAll();
  }

  private triggerFileDownload(blob: Blob, id: number): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `export-job-${id}.${this.getFileExtension()}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  private getFileExtension(): string {
    const type = this.exportJobForm.controls.fileType.value;
    return type === ReportFileType.PDF ? 'zip' : type === ReportFileType.CSV ? 'csv' : '';
  }

  private getStartOfMonth(): Date {
    return DateUtils.getStartOfCurrentMonth();
  }

  private getEndOfMonth(): Date {
    return DateUtils.getEndOfCurrentMonth();
  }
}
